<template>
  <div class="group focus-within:!text-gray-100" :style="divWidth">
    <t-label v-if="label" :opened="opened">{{ label }}</t-label>
    <div ref="outsideAutoComplete" class="relative">
      <div class="cursor-pointer" aria-haspopup="true" :aria-expanded="opened">
        <TInputWithIcon
          ref="autocomplete-input"
          v-model="localSearch"
          :has-icon="hasIcon"
          :disabled="disabled"
          :placeholder="placeholder"
          :opened="opened"
          :error-input="errorInput"
          :error-text="errorText"
          :inverse="inverse"
          @on-focus="onFocus"
          @on-blur="onBlur"
          @keydown="$emit('on-keydown', $event)"
        >
          <template v-if="hasIcon" #icon>
            <!-- Hierarchical dropdown via reka-ui -->
            <DropdownMenuRoot v-if="hasGroupedDropdown" v-model:open="groupedDropdownOpen">
              <DropdownMenuTrigger as-child>
                <button
                  type="button"
                  role="button"
                  class="flex cursor-pointer items-center justify-center rounded bg-structure-1 px-0.5"
                >
                  <MergeTag class="text-white"></MergeTag>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuPortal>
                <DropdownMenuContent
                  class="MT-drop max-h-[300px] overflow-y-auto outline-none font-inter z-[9999] rounded-md shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),_0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)] will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade"
                  :class="{
                    'bg-gray-600 border border-solid border-gray-400': !inverse,
                    'bg-gray-10': inverse,
                  }"
                  :side-offset="5"
                  align="end"
                  :collision-padding="{
                    top: 10,
                    right: 10,
                    bottom: 10,
                    left: 10,
                  }"
                  :avoid-collisions="true"
                  sticky="partial"
                  @close-auto-focus.prevent
                >
                  <DropdownMenuSub>
                    <div
                      class="flex items-center px-3 py-1 border-0 border-b border-solid"
                      :class="{
                        'text-gray-100 border-b-gray-400 bg-gray-600': !inverse,
                        'text-gray-400 border-b-gray-300 bg-gray-10': inverse,
                      }"
                    >
                      <SearchIcon />
                      <input
                        ref="queryInput"
                        v-model="groupedSearchQuery"
                        :placeholder="searchPlaceholder"
                        type="text"
                        class="rounded-0 box-border block w-full border-none p-1 font-inter text-xs leading-[16px] !outline-none transition duration-300 placeholder:text-gray-100"
                        :class="{
                          'bg-gray-600 text-gray-50 focus:!text-gray-10': !inverse,
                          'bg-gray-10 text-gray-400 focus:!text-gray-700': inverse,
                        }"
                        @keydown.stop
                      />
                    </div>
                  </DropdownMenuSub>
                  <div class="p-1">
                    <RecursiveGroup
                      v-for="(item, index) in groupedMTToShow"
                      :key="JSON.stringify(item) + index"
                      :mergetag-item="item"
                      :search-active="!!groupedSearchQuery"
                      :inverse="inverse"
                      @insert="onGroupedSelect"
                    />
                    <div
                      v-if="groupedMTToShow.length === 0"
                      class="px-3 py-1"
                      :class="{ 'text-gray-100': !inverse, 'text-gray-400': inverse }"
                    >
                      {{ noResultsText }}
                    </div>
                  </div>
                </DropdownMenuContent>
              </DropdownMenuPortal>
            </DropdownMenuRoot>

            <!-- Simple button (no grouped merge tags, falls back to flat list) -->
            <button
              v-else
              type="button"
              role="button"
              class="flex cursor-pointer items-center justify-center rounded bg-structure-1 px-0.5"
              @click="onOpenClick"
            >
              <MergeTag class="text-white"></MergeTag>
            </button>
          </template>
        </TInputWithIcon>
      </div>

      <!-- Flat autocomplete dropdown (triggered by typing) -->
      <transition name="slide-fade-dropdown">
        <ul
          v-if="filteredMergeTags.length > 0 && opened"
          ref="dropdown-list"
          class="absolute left-[-1px] top-0 z-10 mt-[36px] max-h-[240px] w-full list-none overflow-hidden rounded-md !border-t-0 p-0 font-inter text-xs font-medium leading-6"
          :class="{ 'bg-gray-600 text-gray-100': !inverse, 'bg-gray-10 text-gray-400': inverse }"
        >
          <VueCustomScrollbar
            :style="{
              height: calculatedScrollHeight + 'px',
              position: 'relative',
            }"
            :settings="{ wheelPropagation: false }"
          >
            <li
              v-for="(option, i) in filteredMergeTags"
              :key="i"
              class="cursor-pointer px-3 py-1"
              :class="{
                'hover:bg-gray-400 hover:text-gray-50': !inverse,
                'hover:bg-[color-mix(in_srgb,var(--c-g100)_50%,var(--c-g10))] hover:text-gray-700': inverse,
              }"
              @click="selectionChanged(option.value)"
            >
              <p
                v-tooltip="{ content: option.label ? option.label : '' }"
                style="margin: 0; padding: 0; display: flex; align-items: center"
                v-html="truncateString(option.value)"
              ></p>
            </li>
          </VueCustomScrollbar>
        </ul>
      </transition>
    </div>
  </div>
</template>
<script lang="ts" setup>
import TLabel from "../TLabel/TLabel.vue";
import TInputWithIcon from "../TInputWithIcon/TInputWithIcon.vue";
import RecursiveGroup from "./RecursiveGroup.vue";
import type { IMergeTag as IGroupedMergeTag, IMergeTagGroup } from "./RecursiveGroup.vue";
import { vTooltip } from "floating-vue";
import "floating-vue/dist/style.css";
import { MergeTag, SearchIcon } from "@topol/icons";
import { PerfectScrollbar as VueCustomScrollbar } from "vue3-perfect-scrollbar";
import { onClickOutside } from "@vueuse/core";
import { ref, watch, computed, PropType, useTemplateRef } from "vue";
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuContent,
  DropdownMenuSub,
} from "reka-ui";

export type IMergeTag = {
  value: string;
  text: string;
  label: string;
  deafultValue?: string;
};

type SmartMergetagPatern = {
  regex: string;
  label: string;
};

export type ISmartMergeTag = {
  enabled: boolean;
  patterns?: Record<string, SmartMergetagPatern>;
  syntax?: {
    start: string;
    end: string;
  };
};

const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },
  selected: {
    type: String,
    default: "",
  },
  label: {
    type: String,
    default: "",
  },
  truncateLength: {
    type: Number,
    default: 45,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: "",
  },
  width: {
    type: String,
    default: "100%",
  },
  flatMergeTags: {
    type: Array as PropType<IMergeTag[]>,
    default: () => [],
  },
  smartMergeTags: {
    type: Object as PropType<ISmartMergeTag>,
    default: () => {
      return { enabled: true, syntax: { start: "*|", end: "|*" } };
    },
  },

  hasIcon: {
    type: Boolean,
    default: false,
  },
  groupedMergeTags: {
    type: Array as PropType<(IGroupedMergeTag | IMergeTagGroup)[]>,
    default: () => [],
  },

  /*
      Decides if autocomplete is triggered immediately after typing
      or only when the merge tag syntax is detected.
    */
  immediate: {
    type: Boolean,
    default: false,
  },
  errorInput: {
    type: Boolean,
    default: false,
  },
  errorText: {
    type: String,
    default: "",
  },
  searchPlaceholder: {
    type: String,
    default: "Search",
  },
  noResultsText: {
    type: String,
    default: "No results",
  },
  inverse: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "on-blur", "on-keydown"]);

const opened = ref(false);
const mergeTags = ref<IMergeTag[]>([]);
const outsideAutoComplete = useTemplateRef("outsideAutoComplete");
const stopWatch = ref(false);
const isDirty = ref(false);
const isFocused = ref(false);

const groupedDropdownOpen = ref(false);
const groupedSearchQuery = ref("");

const hasGroupedDropdown = computed(() => {
  return props.hasIcon && props.groupedMergeTags.length > 0;
});

function flattenGrouped(
  items: (IGroupedMergeTag | IMergeTagGroup)[],
  parentPath?: string,
): IGroupedMergeTag[] {
  const result: IGroupedMergeTag[] = [];
  for (const item of items) {
    if ("items" in item) {
      const newPath = parentPath ? `${parentPath} > ${item.name}` : item.name;
      result.push(...flattenGrouped(item.items, newPath));
    } else {
      result.push({ ...item, parentPath });
    }
  }
  return result;
}

const groupedMTToShow = computed(() => {
  if (!groupedSearchQuery.value) return props.groupedMergeTags;
  const query = groupedSearchQuery.value.toLowerCase();
  return flattenGrouped(props.groupedMergeTags).filter(
    (tag) => tag.value.toLowerCase().includes(query) || tag.text.toLowerCase().includes(query),
  );
});

watch(groupedDropdownOpen, (isOpen) => {
  if (!isOpen) {
    groupedSearchQuery.value = "";
  } else {
    opened.value = false;
  }
});

function onGroupedSelect(item: IGroupedMergeTag) {
  stopWatch.value = true;
  emit("update:modelValue", item.value);
  groupedDropdownOpen.value = false;
}

const flatMergeTags = computed(() => {
  return props.flatMergeTags;
});

const localSearch = computed({
  get() {
    return props.modelValue;
  },
  set(val: string) {
    emit("update:modelValue", val);
  },
});

const divWidth = computed(() => {
  return `width: ${props.width};`;
});

const calculatedScrollHeight = computed(() => {
  if (mergeTags.value.length < 6) {
    return mergeTags.value.length * 32;
  }

  return 240;
});

function demergetify(mergeTag: string) {
  const syntaxStart = props.smartMergeTags.syntax?.start || "*|";
  const syntaxEnd = props.smartMergeTags.syntax?.end || "|*";

  return mergeTag.replace(syntaxStart, "").replace(syntaxEnd, "");
}

const filteredMergeTags = computed(() => {
  if (!isDirty.value) {
    return mergeTags.value;
  }

  return mergeTags.value.filter((item) => {
    return demergetify(item.value)
      .toLowerCase()
      .trim()
      .includes(demergetify(localSearch.value).toLowerCase().trim());
  });
});

watch(localSearch, (n, o) => {
  if (stopWatch.value) {
    stopWatch.value = false;
    return;
  }

  if (!isFocused.value) {
    return;
  }

  if (props.immediate) {
    if (n !== o) {
      isDirty.value = true;
      open();
      return;
    }
  } else {
    if (n && props.smartMergeTags.syntax && n.startsWith(props.smartMergeTags.syntax.start)) {
      isDirty.value = true;
      open();
    } else {
      close();
    }
  }
});

function onOpenClick() {
  isDirty.value = false;
  open();
}

function selectionChanged(option: string) {
  stopWatch.value = true;

  emit("update:modelValue", option);
  opened.value = false;
}

function getMergeTags() {
  mergeTags.value = flatMergeTags.value;
}

function open() {
  if (mergeTags.value.length === 0) {
    getMergeTags();
  }

  opened.value = true;
}

function truncateString(str: string, n = props.truncateLength) {
  return str.length > n ? str.substring(0, n - 1) + "&hellip;" : str;
}

function close() {
  isDirty.value = false;
  opened.value = false;
}

onClickOutside(outsideAutoComplete, () => {
  close();
});

function onFocus() {
  isFocused.value = true;
}

function onBlur() {
  isFocused.value = false;
  emit("on-blur");
}
</script>
<style lang="scss"></style>
